import { computed, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '@environments';
import { LocalStorageKeys } from '@via/models';

import { PermissionAction, PermissionSubject } from '../../models/user';
import { CommonService } from '../../services/common.service';
import { UserDataService } from '../../services/user-data.service';

@Injectable({
  providedIn: 'root',
})
export class SidebarService {
  // disabling because this is just one big array of items and cannot be broken down further
  // eslint-disable-next-line max-lines-per-function
  public menuItems = computed(() => [
    {
      label: 'Advance Search',
      icon: 'pi pi-pw pi-search',
      visible: true,
      /**
       * Appending /search in this route path, because not doing that would result in
       * this menu item getting selected for all case management routes on route change by getActiveMenu function
       */
      routerLink: '/app/case-management/cases/search',
      disabled: false,
      tooltipOptions: {
        tooltipLabel: 'Advance Search',
      },
    },
    {
      label: 'Dashboard',
      icon: 'pi pi-chart-line',
      visible: false,
      routerLink: '/app/dashboard',
      disabled: false,
      tooltipOptions: {
        tooltipLabel: 'Dashboard',
      },
    },
    {
      label: 'Case Management',
      icon: 'pi pi-pw pi-book',
      visible: this.userDataService
        .userAbilities()
        ?.can(PermissionAction.Read, PermissionSubject.viaCore),
      expanded: false,
      items: [
        {
          label: 'Work Queue',
          routerLink: '/app/case-management/work-queue',
          visible: this.userDataService
            .userAbilities()
            ?.can(PermissionAction.Read, PermissionSubject.MyWorkQueue),
        },
        // {
        //   label: 'My Tasks',
        //   routerLink: '/app/case-management/my-tasks',
        //   visible: this.userDataService
        //     .userAbilities()
        //     ?.can(PermissionAction.Read, PermissionSubject.MyTasks),
        // },
        {
          label: 'New Loan',
          routerLink: '/app/case-management/case-form',
          visible: this.userDataService
            .userAbilities()
            ?.can(PermissionAction.Create, PermissionSubject.Case),
        },

        // {
        //   label: 'Referral Intake',
        //   routerLink: '/app/case-management/referrals',
        //   visible: this.userDataService
        //     .userAbilities()
        //     ?.can(PermissionAction.Read, PermissionSubject.ReferralIntake),
        // },
        {
          label: 'Loan Search',
          routerLink: '/app/case-management/cases/search',
          visible: this.userDataService
            .userAbilities()
            ?.can(PermissionAction.Read, PermissionSubject.Case),
        },
        {
          label: 'File Info',
          routerLink: '/app/coming-soon',
          visible: false,
        },
        {
          label: 'Case Info',
          routerLink: '/app/coming-soon',
          visible: false,
        },
        {
          label: 'Manage Participants',
          routerLink: '/app/case-management/participants',
          visible:
            this.userDataService
              .userAbilities()
              ?.can(PermissionAction.Read, PermissionSubject.Participants) &&
            false,
        },
        {
          label: 'Assignments',
          routerLink: '/app/case-management/queues',
          visible: this.userDataService
            .userAbilities()
            ?.can(PermissionAction.View, PermissionSubject.QueueBuilder),
        },
      ],
      tooltipOptions: {
        tooltipLabel: 'Case Management',
      },
    },
    {
      label: 'People',
      icon: 'pi pi-fw pi-users',
      visible: false,
      //  this.userDataService
      //   .userAbilities()
      //   ?.can(PermissionAction.Read, PermissionSubject.viaCore),
      expanded: false,
      items: [
        {
          label: 'Create User Account',
          routerLink: '/app/people/user-form/add',
          visible: this.userDataService
            .userAbilities()
            ?.can(PermissionAction.AddUser, PermissionSubject.ManageUser),
        },
        {
          label: 'Manage Users',
          routerLink: '/app/people/users',
          visible: this.userDataService
            .userAbilities()
            ?.can(PermissionAction.Read, PermissionSubject.ManageUser),
        },
        {
          label: 'Licenses and Certification',
          routerLink: '/app/people/dashboard',
          visible:
            this.userDataService
              .userAbilities()
              ?.can(PermissionAction.Read, PermissionSubject.Dashboard) &&
            false,
        },
        {
          label: 'Manage Department',
          routerLink: '/app/people/departments',
          visible: this.userDataService
            .userAbilities()
            ?.can(PermissionAction.Read, PermissionSubject.ManageDepartment),
        },
        {
          label: 'Dashboard',
          routerLink: '/app/people/dashboard',
          visible: this.userDataService
            .userAbilities()
            ?.can(PermissionAction.Read, PermissionSubject.Dashboard),
        },
        // TODO Hided Menu Item On Temporary Purpose
        {
          label: 'New Dashboard',
          routerLink: '/app/people/dashboard/v2',
          visible: false,
        },
        {
          label: 'Setup',
          routerLink: '/app/people/setup',
          visible: this.userDataService
            .userAbilities()
            ?.can(PermissionAction.Read, PermissionSubject.DashboardQueue),
        },
        {
          label: 'Performance Management',
          routerLink: '/app/coming-soon',
          visible: false,
        },
        {
          label: 'Employee Management',
          routerLink: '/app/people/employees',
          visible: this.userDataService
            .userAbilities()
            ?.can(PermissionAction.Read, PermissionSubject.EmployeeManagement),
        },
        {
          label: 'System Access',
          routerLink: '/app/people/system-access',
          visible: this.userDataService
            .userAbilities()
            ?.can(PermissionAction.Read, PermissionSubject.SystemAccess),
        },
      ],
      tooltipOptions: {
        tooltipLabel: 'People',
      },
    },
    {
      label: 'DocGen',
      icon: 'pi pi-fw pi-folder',
      visible: this.userDataService
        .userAbilities()
        ?.can(PermissionAction.View, PermissionSubject.viaCore),
      expanded: false,
      items: [
        {
          label: 'Template Management',
          routerLink: '/app/doc-gen/template-management',
          visible: this.userDataService
            .userAbilities()
            ?.can(PermissionAction.View, PermissionSubject.viaCore),
        },
      ],

      tooltipOptions: {
        tooltipLabel: 'DocGen',
      },
    },
    {
      label: 'viaDocs',
      icon: 'pi pi-fw pi-file',
      visible: false,
      expanded: false,
      items: [
        { label: 'Search', routerLink: '/app/coming-soon', visible: true },
        {
          label: 'Upload Documents',
          routerLink: '/app/coming-soon',
          visible: true,
        },
      ],
      tooltipOptions: {
        tooltipLabel: 'viaDocs',
      },
    },
    {
      label: 'viaReports',
      icon: 'pi pi-fw pi-chart-bar',
      visible: false,
      expanded: false,
      items: [
        {
          label: 'Edit',
          routerLink: '/app/coming-soon',
          expanded: false,
          items: [{ label: 'Save' }, { label: 'Update' }],
        },
        {
          label: 'Other',
          routerLink: '/app/coming-soon',
          expanded: false,
          items: [{ label: 'Delete' }],
        },
      ],
      tooltipOptions: {
        tooltipLabel: 'viaReports',
      },
    },
    {
      label: 'Outamate Works',
      icon: 'pi pi-fw pi-bars',
      visible:
        this.userDataService
          .userAbilities()
          ?.can(PermissionAction.Read, PermissionSubject.viaWorks) || true,
      expanded: false,
      items: [
        {
          label: 'Workflow Components',
          routerLink: 'workflow-management/workflows',
          visible: this.userDataService
            .userAbilities()
            ?.can(PermissionAction.Read, PermissionSubject.WorkflowComponents),
        },
        {
          label: 'Outamate Works Designer',
          visible: this.userDataService
            .userAbilities()
            ?.can(PermissionAction.Read, PermissionSubject.Designer),
          command: () => {
            window.open(environment.WORKFLOW_URL, '_blank');
          },
        },
        {
          label: 'Variable Library',
          routerLink: 'workflow-management/variables',
          visible: false,
          // visible: this.userDataService
          //   .userAbilities()
          //   ?.can(PermissionAction.Read, PermissionSubject.VariableLibrary),
        },
        // {
        //   label: 'Checklist Library',
        //   routerLink: 'app/workflow-management/checklists',
        //   visible: false,
        // },
      ],
      tooltipOptions: {
        tooltipLabel: 'Outamate Works',
      },
    },
    {
      label: 'viaRules',
      routerLink: '/app/coming-soon',
      visible: false,
      icon: 'pi pi-fw pi-sliders-h',
      expanded: false,
      tooltipOptions: {
        tooltipLabel: 'viaRules',
      },
    },
    {
      label: 'Administration',
      icon: 'pi pi-fw pi-user',
      visible:
        this.userDataService
          .userAbilities()
          ?.can(PermissionAction.Read, PermissionSubject.viaAdmin) && false,
      expanded: false,
      items: [
        {
          label: 'Manage Clients',
          routerLink: '/app/admin/client-management/via-clients',
          visible: this.userDataService
            .userAbilities()
            ?.can(PermissionAction.Read, PermissionSubject.ManageClient),
        },
        {
          label: 'Manage Tenants',
          routerLink: '/app/admin/tenant-management',
          visible: this.userDataService
            .userAbilities()
            ?.can(PermissionAction.Read, PermissionSubject.ManageTenant),
        },
        {
          label: 'Manage Select List',
          routerLink: '/app/admin/edit-management',
          visible: this.userDataService
            .userAbilities()
            ?.can(PermissionAction.Read, PermissionSubject.ManageSelectList),
        },
        {
          label: 'Client Management',
          routerLink: '/app/admin/client-management/clients',
          visible: this.userDataService
            .userAbilities()
            ?.can(PermissionAction.Read, PermissionSubject.ClientManagement),
        },
        {
          label: 'Vendor Management',
          routerLink: '/app/coming-soon',
          visible: false,
        },
      ],
      tooltipOptions: {
        tooltipLabel: 'Administration',
      },
    },
    {
      label: 'Logout',
      icon: 'pi pi-fw pi-sign-out',
      visible: true,
      tooltipOptions: {
        tooltipLabel: 'Logout',
      },
      command: () => {
        sessionStorage.clear();
        localStorage.clear();
        window.location =
          `https://login.microsoftonline.com/common/oauth2/logout?post_logout_redirect_uri=${environment.BASE_URL_HOST}/#/auth/login` as any;
      },
    },
  ]);

  // private preferenceDataSource!: BehaviorSubject<boolean>;
  private _isOpen = signal(false);
  public isOpen = computed(() => this._isOpen());

  private _isAlwaysOpen = signal(false);
  public isAlwaysOpen = computed(() => this._isAlwaysOpen());

  constructor(
    public commonService: CommonService,
    private router: Router,
    private userDataService: UserDataService
  ) {
    this._isAlwaysOpen.set(
      localStorage.getItem(LocalStorageKeys.SIDEBAR_PREFERENCE) === 'true'
    );
    this._isOpen.set(
      localStorage.getItem(LocalStorageKeys.SIDEBAR_PREFERENCE) === 'true'
    );
  }

  toggle() {
    this._isOpen.set(!this._isOpen());
  }

  togglePreference(checked: boolean) {
    localStorage.setItem(LocalStorageKeys.SIDEBAR_PREFERENCE, `${checked}`);
    this._isAlwaysOpen.set(checked);
  }
}
